import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import Button from "../../../../components/essentials/button/Button";
import AcedTextArea from "../../../../components/essentials/input/text-area/AcedTextArea";
import AcedInput from "../../../../components/essentials/input/text/AcedInput";
import { SeasonInfo } from "../../../../shared/models";
import http from "../../../../shared/utils/http";

import ClickIcon from "../../../../components/essentials/icon-buttons/click-icon/ClickIcon";
import DeleteButton from "../../../../components/essentials/icon-buttons/delete-button/DeleteButton";
import "./SeasonCard.scss";

export type SeasonCardProps = {
	mySeason?: boolean;
	info: SeasonInfo;
	register: any;
	removeSeasonAction: any;
	setNewSeason: any;
	isDirty: boolean;
	index: number;
	errors: any;
	teamId?: string;
};

const SeasonCard: React.FC<SeasonCardProps> = ({
	info,
	index,
	removeSeasonAction,
	setNewSeason,
	mySeason,
	teamId,
}) => {
	const [isEdit, setIsEdit] = useState(info.isNew || false);
	const [data, setData] = useState(info);

	const { t } = useTranslation();
	const dispatch = useDispatch();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({
		mode: "onChange",
		defaultValues: {
			year: data.year || "",
			description: data.description || "",
		},
	});

	useEffect(() => {
		reset({
			year: data.year || "",
			description: data.description || "",
		});
	}, [data, reset]);

	const onSubmit = (formData: any) => {
		const seasonData = {
			year: formData.year,
			description: formData.description,
		};

		if (info.isNew && !data.id) {
			// Create new season
			http
				.post(`/teams/season`, seasonData)
				.then((response) => {
					setData(response);
					setIsEdit(false);
					setNewSeason(false);
				})
				.catch((error) => {
					console.error("Error creating season:", error);
				});
		} else {
			// Update existing season
			http
				.patch(`/teams/season/${data.id}`, seasonData)
				.then((response) => {
					setData(response);
					setIsEdit(false);
				})
				.catch((error) => {
					console.error("Error updating season:", error);
				});
		}
	};

	const handleDelete = () => {
		removeSeasonAction(index, data.id);
	};

	const handleEdit = () => {
		setIsEdit(true);
		setNewSeason(false);
	};

	const handleCancel = () => {
		if (info.isNew) {
			removeSeasonAction(index);
		} else {
			setIsEdit(false);
			reset({
				year: data.year || "",
				description: data.description || "",
			});
		}
	};

	if (isEdit) {
		return (
			<div className="season-card season-card--editing">
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="season-card__edit-title">
						{info.isNew ? "Add Season" : "Edit Season"}
					</div>

					<div className="season-card__form-grid">
						<div className="season-card__form-section">
							<div className="season-card__section-title">Season Details</div>
							<div className="season-card__input-field">
								<AcedInput
									placeholder="Year"
									control={control}
									name="year"
									className="season-card__input"
									rules={{
										required: "Year is required",
										pattern: {
											value: /^\d{4}$/,
											message: "Year must be exactly 4 digits",
										},
										validate: (value: string) => {
											const year = parseInt(value);
											const currentYear = new Date().getFullYear();
											if (year < 1900 || year > currentYear + 10) {
												return `Year must be between 1900 and ${currentYear + 10
													}`;
											}
											return true;
										},
									}}
								/>
							</div>
							<div className="season-card__input-field">
								<AcedTextArea
									placeholder="Season description..."
									control={control}
									name="description"
									className="season-card__input"
								/>
							</div>
						</div>
					</div>

					<div className="season-card__button-row">
						<Button
							className="btn btn-secondary"
							action={handleCancel}
							type="button"
						>
							{t("profile.profilePage.cancelBtnLabel")}
						</Button>
						<Button
							className="btn btn-primary"
							type="submit"
						>
							{info.isNew
								? "Add Season"
								: t("common.buttons.edit")}
						</Button>
					</div>
				</form>
			</div>
		);
	}

	return (
		<div className="season-card">
			<div className="season-card__header">
				<div className="season-card__title-section">
					<div className="season-card__year">{data.year}</div>
				</div>
				{mySeason && (
					<div className="season-card__actions">
						<ClickIcon
							icon="edit"
							onClick={handleEdit}
							size={20}
							tooltip="Edit season"
						/>
						<DeleteButton
							onClick={handleDelete}
							//size={20}
							tooltip="Delete season"
						/>
					</div>
				)}
			</div>

			<div className="season-card__content">
				<p className="season-card__description">{data.description}</p>
			</div>
		</div>
	);
};

export default SeasonCard;
